<template>
  <section class="Page" :style="{ height: pageHeight }">
    <Header
      class="Page__header"
      :show-service-menu="false"
    />

    <main class="Page__main">
      <GlobalNavigation
        class="Page__globalNavigation"
        :global-menu-items="globalMenuItems"
      />

      <ScrollBox class="Page__scrollBox">
        <Section class="Page__accountDetails">
          <Heading
            type="secondary"
            level="2"
            :heading="$t('userInfo.title')"
            class="Page__headTitle"
          />
          <Card class="Page__card">
            <section class="Page__section">
              <table class="Page__table">
                <tbody>
                  <tr>
                    <th scope="col" class="Page__heading">
                      <span class="Page__label">
                        {{ $t('userInfo.organizationId') }}
                      </span>
                    </th>
                    <td>
                      <div
                        v-if="getUserDetailsLoading"
                        class="Page__fieldSkeleton shimmer"
                      ></div>
                      <span v-else class="Page__itemDisplay">
                        {{ userInput.organizationId }}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th scope="col" class="Page__heading">
                      <span class="Page__label">
                        {{ $t('userInfo.organizationName') }}
                      </span>
                    </th>
                    <td>
                      <div
                        v-if="getUserDetailsLoading"
                        class="Page__fieldSkeleton shimmer"
                      ></div>
                      <span v-else class="Page__itemDisplay">
                        {{ userInput.organizationName }}
                      </span>
                    </td>
                  </tr>
                  <!-- Hidden: Auth0 has no user name value for this tenant.
                  <tr>
                    <th scope="col" class="Page__heading">
                      <span class="Page__label">
                        {{ $t('userInfo.userName') }}
                      </span>
                    </th>
                    <td>
                      <div
                        v-if="getUserDetailsLoading"
                        class="Page__fieldSkeleton shimmer"
                      ></div>
                      <span v-else class="Page__itemDisplay">
                        {{ userInput.userId }}
                      </span>
                    </td>
                  </tr>
                  -->
                  <!-- Hidden: the display name field is not shown on this screen.
                  <tr>
                    <th scope="col" class="Page__heading">
                      <span class="Page__label">
                        {{ $t('userInfo.displayName') }}
                      </span>
                    </th>
                    <td>
                      <div
                        v-if="getUserDetailsLoading"
                        class="Page__fieldSkeleton shimmer"
                      ></div>
                      <template v-else-if="isEditingName">
                        <div class="Page__itemDiv">
                          <div class="Page__inputItem">
                            <TextField
                              id="displayName"
                              class="Page__textField"
                              type="text"
                              name="displayName"
                              :ariaLabel="$t('userInfo.displayName')"
                              :placeholder="$t('userInfo.displayName')"
                              :value="userInput.displayName"
                              :is-error="isNameError"
                              @input="onChangeName($event)"
                              @blur="onBlurName"
                            />
                            <p v-if="isNameError" class="Page__errorText">
                              {{
                                userInput.displayName.trim() === ''
                                  ? commonMessage.requiredField(
                                      $t('userInfo.displayName'),
                                    )
                                  : commonMessage.lengthValidationError(
                                      $t('userInfo.displayName'),
                                      DISPLAY_NAME_MAX_LENGTH,
                                    )
                              }}
                            </p>
                          </div>
                          <div class="Page__buttonItem">
                            <Button
                              class="Page__button"
                              tag="button"
                              dynamics="secondary"
                              :label="$t('common.cancel')"
                              @click="onCancelDisplayNameEdit"
                            />
                            <Button
                              class="Page__button"
                              tag="button"
                              dynamics="primary"
                              :label="$t('common.change')"
                              :is-disabled="isDisableNameSave"
                              @click="onSaveDisplayName"
                            />
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <div class="Page__itemDisplay">
                          <span>{{ userInput.displayName }}</span>
                          <IconButton
                            class="Page__edit"
                            :text="$t('common.change')"
                            icon-name="Edit"
                            size="xlarge"
                            color="gray"
                            :ariaLabel="$t('common.change')"
                            @click="enableEdit('displayName')"
                          />
                        </div>
                      </template>
                    </td>
                  </tr>
                  -->
                  <tr>
                    <th scope="col" class="Page__heading">
                      <span class="Page__label">
                        {{ $t('userInfo.email') }}
                      </span>
                    </th>
                    <td>
                      <div
                        v-if="getUserDetailsLoading"
                        class="Page__fieldSkeleton shimmer"
                      ></div>
                      <template v-else-if="isEditingEmail">
                        <div class="Page__itemDiv">
                          <div class="Page__inputItem">
                            <TextField
                              id="email"
                              class="Page__textField"
                              type="text"
                              name="email"
                              :ariaLabel="$t('userInfo.email')"
                              :placeholder="$t('userInfo.email')"
                              :value="userInput.email"
                              :is-error="isEmailError"
                              @input="onChangeEmail($event)"
                              @blur="onBlurEmail"
                            />

                            <p v-if="isEmailError" class="Page__errorText">
                              {{
                                userInput.email.trim() === ''
                                  ? commonMessage.requiredField(
                                      $t('userInfo.email'),
                                    )
                                  : isEmailLengthError
                                    ? commonMessage.emailLengthError(
                                        $t('userInfo.email'),
                                        EMAIL_MAX_LENGTH,
                                        EMAIL_LOCAL_PART_MAX_LENGTH,
                                      )
                                    : commonMessage.emailError
                              }}
                            </p>

                            <p class="Page__helperText">
                              {{ $t('userInfo.emailHelp') }}
                            </p>
                          </div>
                          <div class="Page__buttonItem">
                            <Button
                              class="Page__button"
                              tag="button"
                              dynamics="secondary"
                              :label="$t('common.cancel')"
                              @click="onCancelEmailEdit"
                            />
                            <Button
                              class="Page__button"
                              tag="button"
                              dynamics="primary"
                              :label="$t('common.change')"
                              :is-disabled="isDisableEmailSave"
                              @click="onSaveEmail()"
                            />
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <div class="Page__itemDisplay">
                          <span>{{ userInput.email }}</span>
                          <IconButton
                            class="Page__edit"
                            :text="$t('common.change')"
                            icon-name="Edit"
                            size="xlarge"
                            color="gray"
                            :ariaLabel="$t('common.change')"
                            @click="enableEdit('email')"
                          />
                        </div>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>
          </Card>

          <Card class="Page__passwordCard">
            <section class="Page__section">
              <table class="Page__table">
                <tbody>
                  <tr>
                    <th scope="col" class="Page__heading">
                      <span class="Page__label">
                        {{ $t('userInfo.password') }}
                      </span>
                    </th>
                    <td>
                      <div
                        v-if="getUserDetailsLoading"
                        class="Page__fieldSkeleton shimmer"
                      ></div>
                      <div v-else class="Page__itemDisplay">
                        <span class="Page__hideText">•••••••</span>
                        <Button
                          class="Page__button"
                          tag="button"
                          dynamics="tertiaryBlack"
                          :label="$t('userInfo.reset')"
                          @click="passwordResetClick()"
                        />
                      </div>
                      <p class="Page__PasswordHelpText">
                        {{ $t('userInfo.passwordHelp') }}
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>
            <Modal
              v-if="errorDialog"
              ref="modal"
              class="Page__modal"
              :heading="errorHeading"
              :size="modalSize"
              @close-modal="closeErrorModal"
            >
              <Icon class="Page__icon" icon-name="Exception"></Icon>
              <p class="Page__modalText">
                <span v-for="(line, i) in errorContent.split('\n')" :key="i">
                  {{ line
                  }}<br v-if="i < errorContent.split('\n').length - 1" />
                </span>
              </p>
              <div class="Page__modalButton">
                <Button
                  :label="$t('common.close')"
                  dynamics="secondary"
                  tag="button"
                  @click="closeErrorModal"
                />
              </div>
            </Modal>
            <Modal
              v-if="isNeedReLogin"
              ref="modal"
              class="Page__modal"
              :heading="reLoginHeading"
              :size="modalSize"
            >
              <Icon class="Page__icon" icon-name="CheckCircle"></Icon>
              <p class="Page__modalText">
                {{ $t('userInfo.reLoginMessage') }}
              </p>
              <div class="Page__modalButton">
                <Button
                  :label="$t('userInfo.reLogin')"
                  dynamics="primary"
                  tag="button"
                  @click="logoutScreenManually"
                />
              </div>
            </Modal>
          </Card>
        </Section>
      </ScrollBox>
    </main>
  </section>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { buildLoadingPageUrl } from '@/common/buildLoadingPageUrl';
import { useAuth0 } from '@auth0/auth0-vue';
import { useWindowHeight } from '@/composables/useWindowHeight';
import Heading from '@/components/atoms/Heading.vue';
import TextField from '@/components/atoms/TextField.vue';
import ScrollBox from '@/components/atoms/ScrollBox.vue';
import GlobalNavigation from '@/components/organisms/GlobalNavigation.vue';
import Header from '@/components/organisms/Header.vue';
import Card from '@/components/atoms/Card.vue';
import IconButton from '@/components/atoms/IconButton.vue';
import Button from '@/components/atoms/Button.vue';
import Modal from '@/components/organisms/Modal.vue';
import Icon from '@/components/atoms/IconBase.vue';
import { getGlobalMenuItems } from '@/data/SideMenuItems';
import { useGetUserDetails } from '@/graphql/composables/getUserDetails';
import { usePasswordReset } from '@/graphql/composables/passwordReset';
import { useUpdateUserDetails } from '@/graphql/composables/updateUserDetails';
import { useOverlayLoading } from '@/composables/useOverlayLoading';
import {
  // DISPLAY_NAME_MAX_LENGTH,
  EMAIL_LOCAL_PART_MAX_LENGTH,
  EMAIL_MAX_LENGTH,
  emailLengthValidate,
  isSafeUrl,
  // lengthValidate,
  mailValidate,
} from '@/common/validation';
import { appendUiLocales } from '@/common/appendUiLocales';
import { commonMessage } from '@/common/messageContents';
import { Ref, watch, onMounted, onBeforeUnmount } from 'vue';
import type { UserDetailsInput } from '@/graphql/API';

defineOptions({ name: 'AccountManagement' });

const { t, locale } = useI18n();
const { logout } = useAuth0();

type Input = {
  organizationId: string;
  organizationName: string;
  userId: string;
  // displayName: string;
  email: string;
};

const userInput: Ref<Input> = ref({
  organizationId: '',
  organizationName: '',
  userId: '',
  // displayName: '',
  email: '',
});

const { pageHeight } = useWindowHeight();

// let displayName: string;
let emailId: string;
const modalSize = ref('small');
const globalMenuItems = getGlobalMenuItems();

const errorDialog = ref(false);
const errorHeading = ref('');
const errorContent = ref('');

const isNeedReLogin = ref(false);
// Heading of the re-login dialog; set by whichever change succeeded.
const reLoginHeading = ref('');

// const isEditingName = ref(false);
const isEditingEmail = ref(false);

// const isNameError = ref(false);
const isEmailError = ref(false);
const isEmailLengthError = ref(false);

const passwordResetError = t('userInfo.error.passwordReset');
// const nameSaveError = t('userInfo.error.nameSave');
const emailSaveError = t('userInfo.error.emailSave');
const emailAlreadyExistsError = t('userInfo.error.emailAlreadyExists');

// UpdateResponse.statusCode returned by Mutation.updateUserDetails
const UPDATE_STATUS_SUCCESS = 0;
const UPDATE_STATUS_EMAIL_ALREADY_EXISTS = 3;

const { useGetUserDetailsRes, loading: getUserDetailsLoading } =
  useGetUserDetails();
// useOverlayLoading('getUserDetailsLoading', getUserDetailsLoading);

const { mutate: updateUserDetails, loading: mutationLoading } =
  useUpdateUserDetails();
useOverlayLoading('mutationLoading', mutationLoading);

// Hidden: the display name field is not shown on this screen.
/*
const onSaveDisplayName = async () => {
  if (isNameError.value) return;
  errorDialog.value = false;
  // Never save leading/trailing whitespace
  const trimmedDisplayName = userInput.value.displayName.trim();
  userInput.value.displayName = trimmedDisplayName;
  const input = {
    displayName: trimmedDisplayName,
  };
  const response = await saveUserInput(input);
  if (response !== 'Success') {
    errorHeading.value = t('userInfo.displayNameChangeHeading');
    errorContent.value = nameSaveError;
    errorDialog.value = true;
  } else {
    isEditingName.value = false;
    displayName = trimmedDisplayName; //use this value when user click the cancel button in edit mode
    // 表示名はIDトークンのクレームに含まれるため、再ログインするまでヘッダーに
    // 反映されない。メールアドレスと同様に再ログインを促す。
    reLoginHeading.value = t('userInfo.displayNameChangedHeading');
    isNeedReLogin.value = true;
  }
};

const onCancelDisplayNameEdit = () => {
  isEditingName.value = false;
  userInput.value.displayName = displayName;
  isNameError.value = false;
};
*/

const onSaveEmail = async () => {
  if (isEmailError.value) return;
  errorDialog.value = false;
  // Never save leading/trailing whitespace
  const trimmedEmail = userInput.value.email.trim();
  userInput.value.email = trimmedEmail;
  const input = {
    email: trimmedEmail,
  };
  const response = await saveUserInput(input);
  if (response !== 'Success') {
    errorHeading.value = t('userInfo.emailChangeHeading');
    errorContent.value =
      response === 'EmailAlreadyExists'
        ? emailAlreadyExistsError
        : emailSaveError;
    errorDialog.value = true;
  } else {
    isEditingEmail.value = false;
    emailId = trimmedEmail; //use this value when user click the cancel button in edit mode and password reset
    reLoginHeading.value = t('userInfo.emailChangedHeading');
    isNeedReLogin.value = true;
  }
};

const onCancelEmailEdit = () => {
  isEditingEmail.value = false;
  userInput.value.email = emailId;
  isEmailError.value = false;
  isEmailLengthError.value = false;
};

const saveUserInput = async (
  input: UserDetailsInput,
): Promise<'Success' | 'EmailAlreadyExists' | 'Error'> => {
  try {
    const res = await updateUserDetails({
      input: input,
    });

    const statusCode = res?.data?.updateUserDetails?.statusCode;
    if (statusCode === UPDATE_STATUS_SUCCESS) {
      return 'Success';
    }
    if (statusCode === UPDATE_STATUS_EMAIL_ALREADY_EXISTS) {
      return 'EmailAlreadyExists';
    }
    return 'Error';
  } catch (e) {
    console.error(e);
    return 'Error';
  }
};

const enableEdit = (field: 'email') => {
  // if (field === 'displayName') {
  //   isEditingName.value = true;
  // } else if (field === 'email') {
  if (field === 'email') {
    isEditingEmail.value = true;
  }
};

const { mutate: passwordReset, loading: passwordResetLoading } =
  usePasswordReset();
useOverlayLoading('passwordResetLoading', passwordResetLoading);

const passwordResetClick = async () => {
  // Open window immediately to bypass popup blocker
  const newWindow = window.open(
    buildLoadingPageUrl(locale.value, t('userInfo.redirecting')),
    '_blank',
  );
  if (!newWindow) {
    showPasswordResetError();
    return;
  }

  try {
    const res = await passwordReset();

    const url = res?.data?.passwordReset;

    if (url && isSafeUrl(url, import.meta.env.VITE_PASSWORD_RESET_DOMAIN_VAL)) {
      // The Auth0 change-password page follows the browser's Accept-Language,
      // so pin it to the deployment locale (ph: en / vn: vi)
      newWindow?.location.replace(
        appendUiLocales(url, import.meta.env.VITE_APP_AUTH0_LOCALE),
      );
    } else if (url) {
      console.error('Untrusted password reset URL blocked');
      newWindow?.close();
      showPasswordResetError();
    } else {
      newWindow?.close(); // Close the blank tab
      showPasswordResetError();
    }
  } catch (error) {
    console.error('Password reset error:', error);
    newWindow?.close(); // Close the blank tab if error
    showPasswordResetError();
  }
};

const showPasswordResetError = () => {
  errorHeading.value = t('userInfo.passwordResetHeading');
  errorContent.value = passwordResetError;
  errorDialog.value = true;
};

const closeErrorModal = () => {
  errorDialog.value = false;
};

const logoutScreenManually = () => {
  // Auth0 の /v2/logout を経由しないと IdP セッションが残り、
  // SSO で自動再ログインしてメールアドレスの変更が反映されないため url へ遷移する。
  // returnTo を省略すると Auth0 の Allowed Logout URLs の先頭に遷移してしまう
  logout({
    logoutParams: {
      returnTo: `${window.location.origin}/`,
    },
    async openUrl(url) {
      window.location.replace(url);
    },
  });
};

watch(
  () => useGetUserDetailsRes.value,
  (newValue) => {
    emailId = newValue?.email;
    // displayName = newValue?.displayName;
    userInput.value.userId = newValue?.userName;
    userInput.value.organizationId = newValue?.organizationId;
    userInput.value.organizationName = newValue?.organizationName;
    // userInput.value.displayName = newValue?.displayName;
    userInput.value.email = newValue?.email;
  },
);

// Hidden: the display name field is not shown on this screen.
/*
// Surrounding whitespace is trimmed on save, so validate the trimmed value as well
const onChangeName = (name: string) => {
  userInput.value.displayName = name;
  const trimmedName = name.trim();
  isNameError.value =
    trimmedName === '' || !lengthValidate(trimmedName, DISPLAY_NAME_MAX_LENGTH);
};

// Replace the value with its trimmed form when the field loses focus
const onBlurName = () => {
  onChangeName(userInput.value.displayName.trim());
};

const isDisableNameSave = computed(() => {
  return (
    isNameError.value || displayName === userInput.value.displayName.trim()
  );
});
*/

const onChangeEmail = (email: string) => {
  userInput.value.email = email;
  const trimmedEmail = email.trim();
  isEmailLengthError.value = !emailLengthValidate(trimmedEmail);
  isEmailError.value = isEmailLengthError.value || !mailValidate(trimmedEmail);
};

const onBlurEmail = () => {
  onChangeEmail(userInput.value.email.trim());
};

const isDisableEmailSave = computed(() => {
  return isEmailError.value || emailId === userInput.value.email.trim();
});

const updateModalSize = () => {
  modalSize.value = window.innerWidth <= 425 ? 'extraSmall' : 'small';
};

onMounted(() => {
  updateModalSize();
  window.addEventListener('resize', updateModalSize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateModalSize);
});
</script>

<style lang="scss" scoped>
@use '@/assets/scss/_pageForward.scss' as *;

$margin-horizontal: $service-unit * 6;

.Page {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  width: 100%;
  min-width: $service-min-width;

  & &__scrollBox {
    flex: 1;
  }

  &__accountDetails {
    display: flex;
    flex-direction: column;
    padding: $service-unit * 6 $service-unit * 3 $service-unit * 15;
  }

  &__main {
    top: $service-unit * 12;
    position: relative;
    display: flex;
    height: 100%;
    overflow: hidden;
    padding: 0;
  }

  & &__globalNavigation {
    position: relative;
    top: 0;
    height: auto;
  }

  &__section {
    padding: $service-unit * 3;
  }

  &__heading {
    @include text-title;

    width: $service-unit * 45;
    white-space: normal; // Allow text wrapping
    word-break: break-word; // Handle longer words appropriately
  }

  &__label {
    display: flex;
    column-gap: $service-unit;
    align-items: center;
    width: $service-unit * 51;
    font-size: $service-font-size-mp;
    font-weight: bold;
    line-height: $service-line-height-mf;
    text-align: right;
    word-break: break-all;
    overflow-wrap: break-word;
    vertical-align: middle;
  }

  & &__passwordCard,
  &__card {
    margin-top: $service-unit * 4;
  }

  &__table {
    @include table-separate;

    &--border {
      padding-top: $service-unit * 2;
      margin-top: $service-unit * 2;
      border-top: 1px solid $color-gray-30;
    }
  }

  &__itemDiv {
    display: flex;
    gap: $service-unit * 3;
  }

  &__itemDisplay {
    @include text-data;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: $service-unit * 6;
    padding-left: $service-unit * 8;
  }

  &__inputItem {
    @include text-data;
    display: flex;
    padding-bottom: 0px;
    padding-top: 0px;
    flex-direction: column;
    padding-left: $service-unit * 8;
  }

  &__textField {
    width: $service-unit * 80;
  }

  &__helperText {
    width: $service-unit * 80;
    line-height: $service-unit * 5;
    padding-left: 0px;
    margin-top: $service-unit * 2;
    font-size: $service-font-size-ppp;
    word-break: initial;
  }

  &__PasswordHelpText {
    width: $service-unit * 90;
    line-height: $service-unit * 5;
    padding-left: $service-unit * 8;
    font-size: $service-font-size-ppp;
    word-break: initial;
  }

  &__modalText,
  & &__modalButton {
    margin-top: $service-unit * 6;
  }
  & &__modal {
    text-align: center;
  }

  &__modalText {
    font-size: $service-font-size-pp;
    line-height: $service-line-height-mf;
    text-align: center;
  }

  &__icon {
    width: $service-unit * 10;
    margin: $service-unit * 4 $service-unit * 38 0 $service-unit * 38;
    fill: #c33b2e;
  }

  &__button {
    height: $service-unit * 8;
    font-size: $service-font-size-pp;
  }

  &__errorText {
    display: block;
    font-size: $service-font-size-pp;
    color: $color-red-70;
    padding-left: 0px;
  }

  &__hideText {
    color: $color-gray-60;
    letter-spacing: $service-unit;
    font-size: $service-font-size-ff;
  }

  &__fieldSkeleton {
    margin-top: $service-unit * 2;
    margin-left: $service-unit * 8;
    height: $service-unit * 5;
    background-color: $color-gray-20;
    border-radius: $service-border-radius * 2;
  }

  &__buttonItem {
    display: flex;
    gap: $service-unit * 2;
    margin-top: $service-unit;
  }

  @media (max-width: 1024px) {
    &__textField {
      width: $service-unit * 63;
    }
    &__helperText {
      word-break: initial;
      width: $service-unit * 63;
    }
  }

  @media (width: 768px) {
    & &__globalNavigation {
      position: relative;
      top: 0;
      height: auto;
    }

    &__helperText {
      width: $service-unit * 70;
      padding-left: 0;
      width: 100%;
      font-size: $service-font-size-ppp;
      word-break: initial;
    }

    &__PasswordHelpText {
      width: $service-unit * 70;
      padding-left: $service-unit * 3;
    }

    &__button {
      font-size: $service-font-size-pp;
    }

    &__textField {
      width: 100%;
    }

    &__inputItem {
      display: block;
      margin-top: $service-unit * 2;
      padding-left: $service-unit * 4;
    }

    &__itemDisplay {
      padding-left: $service-unit * 4;
      width: 100%;
    }

    &__buttonItem {
      display: flex;
      justify-content: center;
      gap: $service-unit * 2;
      margin-top: $service-unit * 2;
    }

    &__itemDiv {
      display: flex;
      flex-direction: column;
      gap: $service-unit;
      margin-bottom: $service-unit * 3;
    }

    &__errorText {
      padding-left: 0;
      width: 100%;
    }
  }

  @media (max-width: 767px) {
    &__section {
      width: 100%;
    }

    &__main {
      position: relative;
      height: 100%;
      padding-left: $service-unit * 8;
    }

    & &__globalNavigation {
      position: fixed;
      top: $service-unit * 12;
      height: 100%;
    }

    &__fieldSkeleton {
      margin: $service-unit * 2 0;
    }

    &__icon {
      margin-left: $service-unit * 30;
    }

    &__button {
      margin-top: $service-unit * 3;
      font-size: $service-font-size-pp;
    }

    &__table,
    &__table tbody,
    &__table tr,
    &__table th,
    &__table td {
      display: block;
      width: 100%;
    }

    &__heading {
      padding-bottom: $service-unit * 1;
    }

    &__label {
      margin-bottom: $service-unit * 2;
    }

    &__itemDisplay {
      padding-left: $service-unit * 4;
      width: 100%;
    }

    &__itemDiv {
      display: flex;
      flex-direction: column;
      gap: $service-unit;
    }

    &__inputItem {
      display: block;
      margin-top: $service-unit * 2;
      padding-left: $service-unit;
    }

    &__buttonItem {
      display: flex;
      justify-content: center;
      gap: $service-unit * 2;
      margin-bottom: $service-unit;
    }

    &__edit {
      margin-top: $service-unit;
    }

    &__textField {
      width: 100%;
    }

    &__helperText {
      height: $service-unit * 22;
      font-size: $service-font-size-ppp;
      margin-bottom: $service-unit;
      word-break: initial;
    }

    &__hideText {
      position: relative;
      top: $service-unit;
    }

    &__errorText,
    &__helperText,
    &__PasswordHelpText {
      padding-left: 0;
      width: 100%;
    }
  }
}
</style>
